import { useEffect, useState } from "react";
import { useAuth, api } from "../App";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  TrendingDown, 
  ArrowLeft, 
  Mail, 
  Store, 
  Trash2, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  Unplug,
  Gift,
  CreditCard,
  Building2,
  Palette,
  Image
} from "lucide-react";

const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [stores, setStores] = useState([]);  // All connected stores
  const [storeLimit, setStoreLimit] = useState(1);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disconnectingStoreId, setDisconnectingStoreId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  
  // Agency Branding State
  const [agencyBranding, setAgencyBranding] = useState({
    company_name: "",
    logo_url: "",
    primary_color: "#D4F34A"
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [individualStoreEmails, setIndividualStoreEmails] = useState(false);

  useEffect(() => {
    fetchData();
    fetchHealth();
    checkAffiliateStatus();
    fetchAgencyBranding();
  }, []);

  const checkAffiliateStatus = async () => {
    try {
      const res = await api.get("/affiliates/me");
      setIsAffiliate(res.data?.is_affiliate === true);
    } catch (err) {
      setIsAffiliate(false);
    }
  };

  const fetchAgencyBranding = async () => {
    try {
      const res = await api.get("/settings/agency-branding");
      if (res.data?.agency_branding) {
        setAgencyBranding({
          company_name: res.data.agency_branding.company_name || "",
          logo_url: res.data.agency_branding.logo_url || "",
          primary_color: res.data.agency_branding.primary_color || "#D4F34A"
        });
      }
    } catch (error) {
      console.error("Failed to fetch agency branding:", error);
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      await api.put("/settings/agency-branding", agencyBranding);
      toast.success("Branding settings saved");
    } catch (error) {
      console.error("Failed to save branding:", error);
      toast.error("Failed to save branding settings");
    } finally {
      setSavingBranding(false);
    }
  };

  const handleEmailPreferenceToggle = async (receiveIndividual) => {
    setSaving(true);
    try {
      await api.put(`/settings/individual-store-emails?enabled=${receiveIndividual}`);
      setIndividualStoreEmails(receiveIndividual);
      toast.success(receiveIndividual ? "Individual emails enabled" : "Digest mode enabled");
    } catch (error) {
      console.error("Failed to update email preferences:", error);
      toast.error("Failed to update email preferences");
    } finally {
      setSaving(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [storesRes, userRes, billingRes] = await Promise.all([
        api.get("/shopify/stores").catch(() => ({ data: { stores: [], store_limit: 1 } })),
        api.get("/auth/me").catch(() => ({ data: null })),
        api.get("/billing/status").catch(() => ({ data: null }))
      ]);
      
      setStores(storesRes.data?.stores || []);
      setStoreLimit(storesRes.data?.store_limit || 1);
      setBillingStatus(billingRes.data);
      if (userRes.data) {
        setEmailEnabled(userRes.data.email_reports_enabled !== false);
        setIndividualStoreEmails(userRes.data.individual_store_emails === true);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    try {
      const res = await api.get("/health");
      setHealthStatus(res.data);
    } catch (error) {
      console.error("Health check failed:", error);
    }
  };

  const handleEmailToggle = async (enabled) => {
    setSaving(true);
    try {
      await api.put(`/settings/email-reports?enabled=${enabled}`);
      setEmailEnabled(enabled);
      toast.success(enabled ? "Email reports enabled" : "Email reports disabled");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnectStore = async (storeId, shopDomain) => {
    setDisconnectingStoreId(storeId);
    try {
      await api.delete(`/shopify/store/${storeId}`);
      // Remove from local state
      setStores(prev => prev.filter(s => s.store_id !== storeId));
      toast.success(`Disconnected ${shopDomain}`);
    } catch (error) {
      toast.error("Failed to disconnect store");
    } finally {
      setDisconnectingStoreId(null);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/settings/account");
      toast.success("Account deleted");
      logout();
    } catch (error) {
      toast.error("Failed to delete account");
      setDeleting(false);
    }
  };

  // Open Shopify billing management
  const handleOpenBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      // Get connected store info to build Shopify Admin URL
      const res = await api.get("/billing/shopify/status");
      if (res.data?.shop_domain) {
        // Redirect to Shopify Admin billing page
        window.location.href = `https://${res.data.shop_domain}/admin/settings/billing`;
      } else {
        toast.info("Manage your subscription in your Shopify Admin → Settings → Billing", { duration: 5000 });
        setOpeningPortal(false);
      }
    } catch (error) {
      console.error("Billing portal error:", error);
      toast.info("Manage your subscription in your Shopify Admin → Settings → Billing", { duration: 5000 });
      setOpeningPortal(false);
    }
  };

  // Get plan name for display
  const planName = billingStatus?.plan_name || 
    (billingStatus?.plan === 'agency' ? 'Agency' : 'Operator');
  
  // Check if user has an active subscription
  const hasActiveSubscription = billingStatus?.status === 'active' || billingStatus?.status === 'trialing';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D4F34A] flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-black" />
            </div>
            <span className="font-heading font-bold text-xl">ReplyDriven</span>
          </Link>
          
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-text-muted hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Settings</h1>
            <p className="text-text-muted">Manage your account and preferences</p>
          </div>

          <div className="space-y-6">
            {/* Account Info */}
            <Card className="bg-surface border-[#262626]">
              <CardHeader>
                <CardTitle>Account</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-text-muted">{user?.email}</p>
                  </div>
                  {user?.picture && (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-12 h-12 rounded-full"
                    />
                  )}
                </div>
                {/* Subscription Info */}
                <div className="pt-2 border-t border-[#262626]">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-text-muted">Current Plan</span>
                    <span className="font-medium text-[#D4F34A]">{planName}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-text-muted">Connected Stores</span>
                    <span className="font-medium">{stores.length}/{storeLimit}</span>
                  </div>
                </div>
                {/* Affiliate Status */}
                {isAffiliate && (
                  <div className="pt-2 border-t border-[#262626]">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-muted flex items-center gap-1.5">
                        <Gift className="w-4 h-4 text-[#D4F34A]" />
                        Affiliate Account
                      </span>
                      <Link 
                        to="/affiliates/dashboard" 
                        className="text-sm text-[#D4F34A] hover:underline"
                        data-testid="affiliate-link"
                      >
                        Go to Dashboard →
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Subscription Management */}
            {hasActiveSubscription && (
              <Card className="bg-surface border-[#262626]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#D4F34A]" />
                    Subscription
                  </CardTitle>
                  <CardDescription>
                    Manage your billing, update payment method, or cancel
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-text-muted">Current plan</p>
                      <p className="font-semibold text-white">{planName} Plan</p>
                      {billingStatus?.status === 'trialing' && (
                        <p className="text-xs text-[#D4F34A] mt-1">Free trial active</p>
                      )}
                    </div>
                    <Button
                      onClick={handleOpenBillingPortal}
                      disabled={openingPortal}
                      variant="outline"
                      className="border-[#262626] hover:bg-surface"
                      data-testid="manage-subscription-btn"
                    >
                      {openingPortal ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ExternalLink className="w-4 h-4 mr-2" />
                      )}
                      Manage Subscription
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Email Preferences */}
            <Card className="bg-surface border-[#262626]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#D4F34A]" />
                  Email Reports
                </CardTitle>
                <CardDescription>
                  Receive weekly profit leak insights in your inbox
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {emailEnabled ? (
                      <CheckCircle2 className="w-5 h-5 text-[#D4F34A]" />
                    ) : (
                      <XCircle className="w-5 h-5 text-text-muted" />
                    )}
                    <span className={emailEnabled ? "text-white" : "text-text-muted"}>
                      {emailEnabled ? "Reports enabled" : "Reports disabled"}
                    </span>
                  </div>
                  <Switch
                    data-testid="email-toggle"
                    checked={emailEnabled}
                    onCheckedChange={handleEmailToggle}
                    disabled={saving}
                  />
                </div>
                
                {healthStatus?.integrations?.email !== "configured" && (
                  <p className="text-xs text-text-muted mt-3 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-yellow-500" />
                    Email sending disabled (missing RESEND_API_KEY)
                  </p>
                )}
              </CardContent>
            </Card>

            {/* PHASE 3: Downgrade Warning - Show if user has pending downgrade blocked */}
            {billingStatus?.downgrade_warning && (
              <Card className="bg-surface border-[#FF453A]/30 border-2">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-[#FF453A] flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-[#FF453A] mb-1">Downgrade Blocked</h3>
                      <p className="text-text-muted text-sm mb-2">
                        {billingStatus.downgrade_warning.message}
                      </p>
                      <p className="text-xs text-text-muted">
                        You must disconnect {billingStatus.downgrade_warning.excess_stores} store(s) before your plan can be downgraded.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Connected Stores - Multi-Store Support */}
            <Card className="bg-surface border-[#262626]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5 text-[#D4F34A]" />
                  Connected Stores
                </CardTitle>
                <CardDescription>
                  Manage your Shopify store connections ({stores.length}/{storeLimit} connected)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stores.length > 0 ? (
                  <div className="space-y-4">
                    {stores.map((store) => (
                      <div 
                        key={store.store_id}
                        className="flex items-center justify-between py-3 px-4 bg-[#1E1E1E] rounded-lg border border-[#262626]"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                            <Store className="w-5 h-5 text-[#D4F34A]" />
                          </div>
                          <div>
                            <p className="font-medium">{store.shop_domain}</p>
                            <p className="text-xs text-text-muted">
                              Connected {new Date(store.connected_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs bg-[#D4F34A]/20 text-[#D4F34A] px-2 py-1 rounded-full">
                            Active
                          </span>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-text-muted hover:text-[#FF453A] hover:bg-[#FF453A]/10"
                                disabled={disconnectingStoreId === store.store_id}
                              >
                                {disconnectingStoreId === store.store_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Unplug className="w-4 h-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-surface border-[#262626]">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Disconnect {store.shop_domain}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will remove the connection to this store. You can reconnect anytime.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-[#1E1E1E] border-[#262626] hover:bg-[#262626]">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDisconnectStore(store.store_id, store.shop_domain)}
                                  className="bg-[#FF453A] hover:bg-[#FF453A]/90"
                                >
                                  Disconnect
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                    
                    {/* Add Store Button - if under limit */}
                    {stores.length < storeLimit && (
                      <Button
                        onClick={() => navigate("/dashboard")}
                        variant="outline"
                        className="w-full border-dashed border-[#262626] text-text-muted hover:text-white hover:border-[#D4F34A]"
                      >
                        <Store className="w-4 h-4 mr-2" />
                        Connect Another Store ({stores.length}/{storeLimit})
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Store className="w-10 h-10 text-text-muted mx-auto mb-3" />
                    <p className="text-text-muted mb-4">No stores connected</p>
                    <Button
                      onClick={() => navigate("/dashboard")}
                      className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
                    >
                      Connect Store <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Integration Status */}
            <Card className="bg-surface border-[#262626]">
              <CardHeader>
                <CardTitle>Integration Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <StatusRow 
                    label="Shopify" 
                    status={healthStatus?.integrations?.shopify} 
                  />
                  <StatusRow 
                    label="Email (Resend)" 
                    status={healthStatus?.integrations?.email} 
                  />
                  <StatusRow 
                    label="AI Analysis (GPT-5.2)" 
                    status={healthStatus?.integrations?.llm} 
                  />
                </div>
              </CardContent>
            </Card>

            {/* Agency Branding - Only show for agency plan users */}
            {billingStatus?.show_branding && (
              <Card className="bg-surface border-[#262626]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#D4F34A]" />
                    Agency Branding
                  </CardTitle>
                  <CardDescription>
                    White-label your weekly reports with your agency&apos;s brand
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm text-text-muted mb-1.5 block">Company Name</label>
                    <Input
                      placeholder="Your Agency Name"
                      value={agencyBranding.company_name}
                      onChange={(e) => setAgencyBranding(prev => ({ ...prev, company_name: e.target.value }))}
                      className="bg-[#0A0A0A] border-[#262626] focus:border-[#D4F34A]"
                      data-testid="branding-company-name"
                    />
                    <p className="text-xs text-text-muted/60 mt-1">
                      Replaces &quot;ReplyDriven&quot; in client emails
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm text-text-muted mb-1.5 block">Logo URL</label>
                    <Input
                      placeholder="https://youragency.com/logo.png"
                      value={agencyBranding.logo_url}
                      onChange={(e) => setAgencyBranding(prev => ({ ...prev, logo_url: e.target.value }))}
                      className="bg-[#0A0A0A] border-[#262626] focus:border-[#D4F34A]"
                      data-testid="branding-logo-url"
                    />
                    <p className="text-xs text-text-muted/60 mt-1">
                      Appears in email header (recommended: 200x50px PNG)
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm text-text-muted mb-1.5 block">Primary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={agencyBranding.primary_color}
                        onChange={(e) => setAgencyBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer border border-[#262626]"
                        data-testid="branding-primary-color"
                      />
                      <Input
                        value={agencyBranding.primary_color}
                        onChange={(e) => setAgencyBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="bg-[#0A0A0A] border-[#262626] focus:border-[#D4F34A] w-32 font-mono"
                        placeholder="#D4F34A"
                      />
                    </div>
                    <p className="text-xs text-text-muted/60 mt-1">
                      Used for buttons and highlights in emails
                    </p>
                  </div>

                  <Button
                    onClick={handleSaveBranding}
                    disabled={savingBranding}
                    className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
                    data-testid="save-branding-btn"
                  >
                    {savingBranding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Branding
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Email Digest Preferences - Only show for agency plan users with multiple stores */}
            {billingStatus?.show_portfolio && stores.length > 1 && (
              <Card className="bg-surface border-[#262626]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#D4F34A]" />
                    Email Digest Settings
                  </CardTitle>
                  <CardDescription>
                    Choose how you receive weekly reports for your stores
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">Individual Store Emails</p>
                      <p className="text-sm text-text-muted">
                        {individualStoreEmails 
                          ? "Receive a separate email for each store" 
                          : "Receive one consolidated digest email (recommended)"}
                      </p>
                    </div>
                    <Switch
                      data-testid="individual-emails-toggle"
                      checked={individualStoreEmails}
                      onCheckedChange={handleEmailPreferenceToggle}
                      disabled={saving}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Danger Zone */}
            <Card className="bg-surface border-[#FF453A]/30">
              <CardHeader>
                <CardTitle className="text-[#FF453A] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Danger Zone
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full border-[#FF453A] text-[#FF453A] hover:bg-[#FF453A] hover:text-white"
                      data-testid="delete-account-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-surface border-[#262626]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-[#FF453A]">Delete Account?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. All your data, connected stores, and analysis reports will be permanently deleted.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-[#1E1E1E] border-[#262626] hover:bg-[#262626]">
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        className="bg-[#FF453A] hover:bg-[#FF453A]/90"
                        disabled={deleting}
                      >
                        {deleting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Delete Forever"
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

// Status Row Component
const StatusRow = ({ label, status }) => {
  const isConfigured = status === "configured";
  
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-text-muted">{label}</span>
      <span className={`flex items-center gap-1 text-sm ${isConfigured ? 'text-[#D4F34A]' : 'text-text-muted'}`}>
        {isConfigured ? (
          <><CheckCircle2 className="w-4 h-4" /> Configured</>
        ) : (
          <><XCircle className="w-4 h-4" /> Not configured</>
        )}
      </span>
    </div>
  );
};

export default Settings;
